import React, { useCallback, useEffect, useRef, useState } from 'react';
import Slider from 'rc-slider';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './LogoEditor.module.scss';
import Spinner from '../../../../../../../common/components/Spinner';
import VideoService from '../../../../../../service/VideoService';
import { CustomButton } from '../../../../../../components/Button/Button';
import UploadLogoModal from '../UploadLogo/UploadLogoModal';
import Icon from '../../../../../../components/Icon/Icon';
import { ToastNotification } from '../../../../../../components/Toast/Toast';
import SelectOption from '../../../../../../components/SelectOption/SelectOption';
import useObjectSimulation from '../../../../../../hooks/useObjectSimulation';
import { useUserContext } from '../../../../../../context/UserContext';
import { AVAILABLE_POSITIONS } from '../../../../../../../common/constants/common';
import { useNodeRef } from 'engage/hooks/useNodeRef';

const PREVIEW_IDS = {
  landscape: 'landscape-logo-preview',
  portrait: 'portrait-logo-preview',
  square: 'square-logo-preview',
};

const SIZE_MIN_VALUE = 1;
const SIZE_MAX_VALUE = 100;

const MARGIN_MIN_VALUE = 0;
const MARGIN_MAX_VALUE = 50;

const LogoEditor = ({ logoId = null, onClose, companyId }) => {
  const [currentLogo, setCurrentLogo] = useState({});
  const [initialCurrentLogo, setInitialCurrentLogo] = useState({});

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isChanged, setIsChanged] = useState(false);

  const [imageDimensions, setImageDimensions] = useState(null);

  const { updateUserAppConfig } = useUserContext();

  const { t } = useTranslation();

  const history = useHistory();

  const { nodeRef: landscapePreviewNode, handleNodeRef: handleLandscapeNodeRef } = useNodeRef();

  const { objectPosition: landscapePosition, objectSize: landscapeSize, loading: landscapeLoading } = useObjectSimulation({
    parentNode: landscapePreviewNode,
    id: PREVIEW_IDS.landscape,
    mode: 'logo',
    margin_x: currentLogo?.marginX,
    margin_y: currentLogo?.marginY,
    position: currentLogo?.position,
    size: currentLogo?.size,
    imageDimensions,
    isLandscapePreview: true,
  });
  const { nodeRef: portraitPreviewNode, handleNodeRef: handlePortraitNodeRef } = useNodeRef();

  const { objectPosition: portraitPosition, objectSize: portraitSize, loading: portraitLoading } = useObjectSimulation({
    id: PREVIEW_IDS.portrait,
    parentNode: portraitPreviewNode,
    mode: 'logo',
    margin_x: currentLogo?.marginX,
    margin_y: currentLogo?.marginY,
    position: currentLogo?.position,
    size: currentLogo?.size,
    imageDimensions,
    isPortraitPreview: true,
  });
  const { nodeRef: squarePreviewNode, handleNodeRef: handleSquareNodeRef } = useNodeRef();

  const { objectPosition: squarePosition, objectSize: squareSize, loading: squareLoading } = useObjectSimulation({
    parentNode: squarePreviewNode,
    id: PREVIEW_IDS.square,
    mode: 'logo',
    margin_x: currentLogo?.marginX,
    margin_y: currentLogo?.marginY,
    position: currentLogo?.position,
    size: currentLogo?.size,
    imageDimensions,
    isSquarePreview: true,
  });

  const getLogo = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await VideoService.getLogo(logoId, companyId);
      setCurrentLogo(result);
      setInitialCurrentLogo(result);
    } catch (err) {
      console.log(err);
      onClose();
    }

    setIsLoading(false);
  }, [logoId, companyId]);

  const getLogoNameFromURL = url => {
    const nameRegex = /\/logos\/([^?]+)\??/;
    const name = url.match(nameRegex)[1];
    if (!name) {
      throw new Error('Unable to get name from logo to update.');
    }
    return name;
  };

  const handleUpdateNumberInput = (key, value) => {
    if (value === '') {
      handleUpdateCurrentLogo({ [key]: MARGIN_MIN_VALUE });
      return;
    }

    const number = Number(value);
    const isNumber = Number.isInteger(number);

    if (!isNumber) {
      return;
    }

    if ((key === 'marginX' || key === 'marginY') && number > MARGIN_MAX_VALUE) {
      return;
    }

    if (key === 'size' && (number < SIZE_MIN_VALUE || number > SIZE_MAX_VALUE)) {
      return;
    }

    handleUpdateCurrentLogo({ [key]: number });
  };

  const handleUpdateCurrentLogo = changes => {
    setCurrentLogo(prevState => ({ ...prevState, ...changes }));
    setIsChanged(true);
  };

  const handleSave = useCallback(async () => {
    try {
      setIsLoading(true);
      let newFilePath = null;

      // TemporaryUrl exists only if the user has changed the original logo image
      if (currentLogo?.temporaryUrl) {
        const name = getLogoNameFromURL(currentLogo.url);
        newFilePath = await VideoService.uploadLogo(currentLogo.image, name, companyId);
      }

      const fileName = newFilePath ? currentLogo?.image?.name : null;

      const payload = {
        ...(newFilePath && { filePath: newFilePath }),
        position: currentLogo?.position,
        size: Number(currentLogo?.size) === 0 ? SIZE_MIN_VALUE : Number(currentLogo?.size),
        marginX: Number(currentLogo?.marginX),
        marginY: Number(currentLogo?.marginY),
      };

      const updatedLogo = await VideoService.updateLogo(logoId, fileName, payload, companyId);
      setCurrentLogo(updatedLogo);

      ToastNotification({
        message: t('company.settings.logos.update.success'),
        iconName: 'success',
      });
      setIsChanged(false);
      updateUserAppConfig();
    } catch (err) {
      console.log(err);
      ToastNotification({ message: t('company.settings.logos.update.error') });
    }

    setIsLoading(false);
  }, [companyId, logoId, currentLogo]);

  const handleCancel = () => {
    setCurrentLogo(initialCurrentLogo);
    setIsChanged(false);
  };

  const isCenterPosition = currentLogo?.position === 'tc' || currentLogo?.position === 'bc';

  useEffect(() => {
    if (logoId) {
      getLogo();
    }
  }, []);

  useEffect(() => {
    const unblock = isChanged
      ? history.block(() => {
        // Navigation was blocked! Let's show a confirmation dialog
        // so the user can decide if they actually want to navigate
        // away and discard changes they've made in the current page.
        return confirm(t('clip_organizer.confirm_leave_page'));
      })
      : history.block(() => true);

    return () => unblock();
  }, [isChanged]);

  useEffect(() => {
    if (currentLogo?.temporaryUrl) {
      const image = new Image();

      image.onload = () => {
        setImageDimensions({ width: image.width || 0, height: image.height || 0 });
      };
      image.src = currentLogo?.temporaryUrl;
      return;
    }

    if (currentLogo?.url) {
      const image = new Image();

      image.onload = () => {
        setImageDimensions({ width: image.width || 0, height: image.height || 0 });
      };
      image.src = currentLogo?.url;
    }
  }, [currentLogo?.url, currentLogo?.temporaryUrl]);

  return (
    <div className={styles.mainContainer}>
      {isLoading && !currentLogo?.id ? (
        <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />
      ) : (
        <>
          <div className={styles.infoContainer}>
            <div className={styles.backContainer}>
              <CustomButton onClick={onClose} variant="link" className={styles.backButton}>
                <Icon src="arrowLeftStoryboard" className={styles.leftArrowIcon} />
                <span className={styles.btnLabel}>{t('general.back')}</span>
              </CustomButton>
            </div>
            <span className={styles.logoTitle}>{t('company.settings.logos.update.title')}</span>
            <div className={styles.logoContainer}>
              <div className={styles.logoImageContainer}>
                <img className={styles.logoImage} src={currentLogo?.temporaryUrl || currentLogo?.url} alt={t('logo_chooser.header')} />
              </div>
              <CustomButton
                variant="secondary"
                label={t('company.settings.logos.update.replace_logo')}
                className={styles.logoImageBtn}
                onClick={() => setIsUploadModalOpen(true)}
              />
            </div>
            <div className={styles.sizeContainer}>
              <span className={styles.sizeTitle}>{t('company.settings.logos.update.size')}</span>
              <div className={styles.sizeValuesContainer}>
                <div className={styles.sizeInputContainer}>
                  <span className={styles.inputText}>
                    {t('company.settings.logos.update.logo_size')}, <span>%</span>
                  </span>
                  <input
                    type="text"
                    className={styles.inputField}
                    value={currentLogo?.size}
                    onChange={event => handleUpdateNumberInput('size', event.target?.value)}
                  />
                </div>
                <Slider
                  min={SIZE_MIN_VALUE}
                  max={SIZE_MAX_VALUE}
                  step={1}
                  onChange={value => handleUpdateCurrentLogo({ size: value })}
                  value={currentLogo?.size}
                  included
                  style={{ left: '-6px' }}
                  handleStyle={{
                    width: '18px',
                    height: '18px',
                    background: '#FFFFFF',
                    position: 'relative',
                    top: '-10px',
                    borderRadius: '9px',
                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                    cursor: 'pointer',
                  }}
                  trackStyle={{
                    background: 'none',
                    height: '4px',
                    borderRadius: '2px',
                    position: 'relative',
                    top: '-4px',
                  }}
                  railStyle={{
                    background: '#EEEEEE',
                    height: '4px',
                    borderRadius: '2px',
                    position: 'relative',
                  }}
                  marks={{
                    [SIZE_MIN_VALUE]: ' ',
                    [SIZE_MAX_VALUE]: ' ',
                  }}
                  dotStyle={{
                    marginLeft: '2px',
                    bottom: '4px',
                    width: '12px',
                    height: '12px',
                    border: 'none',
                    background: '#EEEEEE',
                  }}
                />
              </div>
            </div>
            <div className={styles.positionContainer}>
              <span className={styles.positionTitle}>{t('company.settings.logos.update.position')}</span>
              <div className={styles.positionValuesContainer}>
                <div className={styles.positionSelectContainer}>
                  <span className={styles.inputText}>{t('company.settings.logos.update.position')}</span>
                  <SelectOption
                    currentlySelected={AVAILABLE_POSITIONS.find(({ type }) => type === currentLogo?.position)}
                    options={AVAILABLE_POSITIONS}
                    onSelect={({ type }) => handleUpdateCurrentLogo({ position: type })}
                    currentInputClassName={styles.currentSelect}
                  />
                </div>
                {!isCenterPosition && (
                  <div className={styles.marginContainer}>
                    <div className={styles.marginInputContainer}>
                      <span className={styles.inputText}>
                        {t('company.settings.logos.update.margin_x')}, <span>%</span>
                      </span>
                      <input
                        type="text"
                        className={styles.inputField}
                        value={currentLogo?.marginX}
                        onChange={event => handleUpdateNumberInput('marginX', event.target?.value)}
                      />
                    </div>
                    <Slider
                      min={MARGIN_MIN_VALUE}
                      max={MARGIN_MAX_VALUE}
                      step={1}
                      onChange={value => handleUpdateCurrentLogo({ marginX: value })}
                      value={currentLogo?.marginX}
                      included
                      style={{ left: '-6px' }}
                      handleStyle={{
                        width: '18px',
                        height: '18px',
                        background: '#FFFFFF',
                        position: 'relative',
                        top: '-10px',
                        borderRadius: '9px',
                        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                        cursor: 'pointer',
                      }}
                      trackStyle={{
                        background: 'none',
                        height: '4px',
                        borderRadius: '2px',
                        position: 'relative',
                        top: '-4px',
                      }}
                      railStyle={{
                        background: '#EEEEEE',
                        height: '4px',
                        borderRadius: '2px',
                        position: 'relative',
                      }}
                      marks={{
                        [MARGIN_MIN_VALUE]: ' ',
                        [MARGIN_MAX_VALUE]: ' ',
                      }}
                      dotStyle={{
                        marginLeft: '2px',
                        bottom: '4px',
                        width: '12px',
                        height: '12px',
                        border: 'none',
                        background: '#EEEEEE',
                      }}
                    />
                  </div>
                )}
                <div className={styles.marginContainer}>
                  <div className={styles.marginInputContainer}>
                    <span className={styles.inputText}>
                      {t('company.settings.logos.update.margin_y')}, <span>%</span>
                    </span>
                    <input
                      type="text"
                      className={styles.inputField}
                      value={currentLogo?.marginY}
                      onChange={event => handleUpdateNumberInput('marginY', event.target?.value)}
                    />
                  </div>
                  <Slider
                    min={MARGIN_MIN_VALUE}
                    max={MARGIN_MAX_VALUE}
                    step={1}
                    onChange={value => handleUpdateCurrentLogo({ marginY: value })}
                    value={currentLogo?.marginY}
                    included
                    style={{ left: '-6px' }}
                    handleStyle={{
                      width: '18px',
                      height: '18px',
                      background: '#FFFFFF',
                      position: 'relative',
                      top: '-10px',
                      borderRadius: '9px',
                      boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
                      cursor: 'pointer',
                    }}
                    trackStyle={{
                      background: 'none',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                      top: '-4px',
                    }}
                    railStyle={{
                      background: '#EEEEEE',
                      height: '4px',
                      borderRadius: '2px',
                      position: 'relative',
                    }}
                    marks={{
                      [MARGIN_MIN_VALUE]: ' ',
                      [MARGIN_MAX_VALUE]: ' ',
                    }}
                    dotStyle={{
                      marginLeft: '2px',
                      bottom: '4px',
                      width: '12px',
                      height: '12px',
                      border: 'none',
                      background: '#EEEEEE',
                    }}
                  />
                </div>
              </div>
            </div>
            <div className={styles.footerContainer}>
              <CustomButton
                variant="primary"
                label={t('general.save')}
                className={styles.footerBtn}
                onClick={handleSave}
                disabled={!isChanged}
                loadingSpinner={true}
                loading={isLoading}
              />
              <CustomButton
                variant="secondary"
                label={t('general.cancel')}
                className={styles.footerBtn}
                onClick={handleCancel}
                disabled={!isChanged || isLoading}
              />
            </div>
          </div>
          <div className={styles.previewContainer}>
            <span className={styles.previewTitle}>{t('company.settings.logos.update.preview')}</span>
            <div ref={handleLandscapeNodeRef} id={PREVIEW_IDS.landscape} className={styles.landscapePreview}>
              {!landscapeLoading && (
                <div style={{ ...landscapePosition, position: 'absolute' }}>
                  <img src={currentLogo?.temporaryUrl || currentLogo?.url} style={{ ...landscapeSize }} alt={`${t('storyboard.generating.orientation.landscape')} ${t('company.settings.logos.update.preview')}`} />
                </div>
              )}
            </div>
            <div className={styles.squarePortraitContainer}>
              <div ref={handlePortraitNodeRef} id={PREVIEW_IDS.portrait} className={styles.portraitPreview}>
                {!portraitLoading && (
                  <div style={{ ...portraitPosition, position: 'absolute' }}>
                    <img src={currentLogo?.temporaryUrl || currentLogo?.url} style={{ ...portraitSize }} alt={`${t('storyboard.generating.orientation.portrait')} ${t('company.settings.logos.update.preview')}`} />
                  </div>
                )}
              </div>
              <div className={styles.squarePreviewContainer}>
                <div ref={handleSquareNodeRef} id={PREVIEW_IDS.square} className={styles.squarePreview}>
                  {!squareLoading && (
                    <div style={{ ...squarePosition, position: 'absolute' }}>
                      <img src={currentLogo?.temporaryUrl || currentLogo?.url} style={{ ...squareSize }} alt={`${t('storyboard.generating.orientation.square')} ${t('company.settings.logos.update.preview')}`} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <UploadLogoModal
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            logoId={logoId}
            logoPath={currentLogo.path}
            onUpload={handleUpdateCurrentLogo}
          />
        </>
      )}
    </div>
  );
};

export default LogoEditor;
